import React, {useState} from 'react';
import {Link} from '@umijs/max';
import {Card, List, Typography, Menu} from 'antd';
import {
  FileTextOutlined,
  CalculatorOutlined,
  ShoppingOutlined,
  ToolOutlined,
  FireOutlined,
  FormOutlined,
  QrcodeOutlined,
  UserOutlined,
  InboxOutlined,
  IdcardOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  MoneyCollectOutlined,
  FileTextTwoTone,
  CreditCardOutlined,
  MessageOutlined,
  FunnelPlotOutlined,
  TeamOutlined,
  FileDoneOutlined,
  ContainerOutlined,
  BookOutlined,
  CalendarOutlined,
  SafetyOutlined,
  WechatOutlined,
  MobileOutlined,
  PhoneOutlined,
  SearchOutlined
} from '@ant-design/icons';
import ProCard from "@ant-design/pro-card";
import './index.less'

// 定义功能项接口
interface FunctionItem {
  id: string;
  title: string;
  desc: string;
  icon: React.ReactNode;
}

// 定义分类接口
interface Category {
  key: string;
  title: string;
  icon: React.ReactNode;
  items: FunctionItem[];
}

// 样式常量
const styles = {
  background: '#fff',
  cardShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
  hoverShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
  primaryColor: '#1677ff',
  secondaryColor: '#e8f3ff',
  neutralColor: '#f5f7fa',
  textColor: {
    primary: '#1f2329',
    secondary: '#6e7072',
    tertiary: '#86909c'
  }
};

// 物业服务
const PropertyServiceItems: FunctionItem[] = [
  {
    id: 'functionServices/propertyService/onlineFiles',
    title: '在线文件管理',
    desc: '管理小区各类文件资料，支持在线查看与下载',
    icon: <FileTextOutlined/>,
  },
  {
    id: 'functionServices/propertyService/meterReading',
    title: '抄表录入',
    desc: '记录水电气表数据，生成历史记录与统计报表',
    icon: <CalculatorOutlined/>
  },
  {
    id: 'functionServices/propertyService/expressDelivery',
    title: '快递管理',
    desc: '快递收发登记，通知业主取件，快递存放管理',
    icon: <ShoppingOutlined/>
  },
  {
    id: 'functionServices/propertyService/onlineRepair',
    title: '在线报修',
    desc: '业主提交维修申请，跟踪维修进度，评价服务质量',
    icon: <ToolOutlined/>
  },
  {
    id: 'functionServices/propertyService/waterElectricityCoalReporting',
    title: '水电煤上报',
    desc: '业主自主上报水电煤使用情况，方便费用核算',
    icon: <FireOutlined/>
  },
  {
    id: 'functionServices/propertyService/complaint',
    title: '投诉建议',
    desc: '接收业主反馈与建议，跟进处理并回复结果',
    icon: <FormOutlined/>
  },
  {
    id: 'functionServices/propertyService/inspectionCode',
    title: '智慧巡检码',
    desc: '物业巡检记录数字化，扫码查看巡检情况',
    icon: <QrcodeOutlined/>
  },
  {
    id: 'functionServices/propertyService/visitorRegistration',
    title: '访客登记',
    desc: '记录访客信息，管理访问权限与时间',
    icon: <UserOutlined/>
  },
  {
    id: 'functionServices/propertyService/material',
    title: '物料管理',
    desc: '物业物资采购、库存与领用管理',
    icon: <InboxOutlined/>
  },
  {
    id: 'functionServices/propertyService/accessCard',
    title: '出入证管理',
    desc: '办理与管理小区出入证件，确保安全管理',
    icon: <IdcardOutlined/>
  },
  {
    id: 'functionServices/propertyService/mobileMeterReading',
    title: '移动抄表',
    desc: '移动端抄表工具，提高抄表效率与准确性',
    icon: <ClockCircleOutlined/>
  },
];

// 小区运营
const CommunityOperationItems: FunctionItem[] = [
  {
    id: 'functionServices/communityOperation/customerService',
    title: '客服IM',
    desc: '在线客服系统，实时解答业主疑问',
    icon: <PhoneOutlined/>
  },
  {
    id: 'functionServices/communityOperation/ownerVoting',
    title: '业主投票',
    desc: '发起业主投票，收集业主意见和决策',
    icon: <FunnelPlotOutlined/>
  },
  {
    id: 'functionServices/communityOperation/smsBroadcast',
    title: '短信群发',
    desc: '向业主批量发送通知、提醒等短信',
    icon: <MobileOutlined/>
  },
  {
    // wechatWork
    id: '',
    title: '企业微信',
    desc: '通过企业微信进行业主沟通与服务',
    icon: <WechatOutlined/>
  },
];

// 小区缴费功能项
const CommunityPaymentItems: FunctionItem[] = [
  {
    id: 'functionServices/communityPayment/depositManagement',
    title: '押金管理',
    desc: '管理业主各类押金的缴纳与退还记录',
    icon: <MoneyCollectOutlined/>
  },
  {
    id: 'functionServices/communityPayment/eInvoice',
    title: '电子发票',
    desc: '开具、管理和查询电子发票',
    icon: <FileTextTwoTone/>
  },
  {
    id: 'functionServices/communityPayment/onlinePayment',
    title: '在线缴费',
    desc: '支持业主在线缴纳物业费等各项费用',
    icon: <CreditCardOutlined/>
  },
];

// 街道/社区功能项
const StreetCommunityItems: FunctionItem[] = [
  {
    id: 'functionServices/streetOrCommunities',
    title: '业主管理',
    desc: '管理业主信息，维护业主档案',
    icon: <UserOutlined/>
  },
  {
    id: 'functionServices/streetOrCommunities',
    title: '留言建议',
    desc: '接收和处理居民的留言与建议',
    icon: <MessageOutlined/>
  },
  {
    id: 'functionServices/streetOrCommunities',
    title: '投票管理',
    desc: '组织和管理社区各类投票活动',
    icon: <FunnelPlotOutlined/>
  },
  {
    id: 'functionServices/streetOrCommunities',
    title: '志愿者活动管理',
    desc: '组织和管理社区志愿者活动',
    icon: <TeamOutlined/>
  },
  {
    id: 'functionServices/streetOrCommunities',
    title: '街道新闻',
    desc: '发布和管理街道社区相关新闻资讯',
    icon: <FileDoneOutlined/>
  },
  {
    id: 'functionServices/streetOrCommunities',
    title: '网格化管理',
    desc: '社区网格化管理，责任到人',
    icon: <ContainerOutlined/>
  },
  {
    id: 'functionServices/streetOrCommunities',
    title: '智慧党课',
    desc: '在线党课学习与管理系统',
    icon: <BookOutlined/>
  },
  {
    id: 'functionServices/streetOrCommunities',
    title: '三会一课',
    desc: '党组织三会一课制度落实与记录',
    icon: <CalendarOutlined/>
  },
  {
    id: 'functionServices/streetOrCommunities',
    title: '综合治理',
    desc: '社区综合治安管理与服务',
    icon: <SafetyOutlined/>
  },
];

// 所有分类
const categories: Category[] = [
  {
    key: 'property-service',
    title: '物业服务',
    icon: <ToolOutlined/>,
    items: PropertyServiceItems
  },
  {
    key: 'community-operation',
    title: '小区运营',
    icon: <TeamOutlined/>,
    items: CommunityOperationItems
  },
  {
    key: 'community-payment',
    title: '小区缴费',
    icon: <CreditCardOutlined/>,
    items: CommunityPaymentItems
  },
  {
    key: 'street-community',
    title: '街道/社区',
    icon: <HomeOutlined/>,
    items: StreetCommunityItems
  }
];

// 功能列表项组件-物业服务跳转等
const FunctionItemCard: React.FC<{ item: FunctionItem }> = ({item}) => {
  return (
    <List.Item
      style={{
        padding: '20px',
        borderRadius: '8px',
        transition: 'all 0.3s ease',
      }}
    >
      <Link
        to={`${item.id}`}
      >
        <List.Item.Meta
          avatar={
            <div style={{
              fontSize: '24px',
              color: styles.primaryColor,
              padding: '12px',
              backgroundColor: styles.secondaryColor,
              borderRadius: '8px',
              transition: 'all 0.3s ease'
            }}>
              {item.icon}
            </div>
          }
          title={
            <div
              style={{
                fontSize: '16px',
                fontWeight: 500,
                color: styles.textColor.primary,
                transition: 'color 0.3s ease',
              }}
            >
              {item.title}
            </div>
          }
          description={
            <Typography.Text
              type="secondary"
              style={{
                marginTop: 8,
                display: 'block',
                color: styles.textColor.secondary,
                lineHeight: 1.5
              }}
            >
              {item.desc}
            </Typography.Text>
          }
        />
      </Link>
    </List.Item>
  );
};

// 功能列表组件（接收搜索参数和回调）
const FunctionList: React.FC<{
  title: string;
  items: FunctionItem[];
  isLoading: boolean;
  searchQuery: string;
  onSearch: (value: string) => void;
}> = ({title, items, isLoading, searchQuery, onSearch}) => {
  // 基于搜索关键词筛选
  const filteredItems = items.filter(item =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.desc.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div
      className="function-list-section"
      style={{
        animation: 'fadeIn 0.5s ease-in-out',
        padding: '16px 0'
      }}
    >
      <div
        style={{
          marginBottom: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography.Title
          level={5}
          style={{
            margin: 0,
            color: styles.textColor.primary,
            fontWeight: 600
          }}
        >
          {title}
        </Typography.Title>
        <Typography.Text type="secondary">
          {filteredItems.length} 个功能
        </Typography.Text>
      </div>

      <Card
        style={{
          boxShadow: styles.cardShadow,
          transition: 'box-shadow 0.3s ease'
        }}
        hoverable
      >
        <div style={{marginBottom: 16}}>
          <div
            style={{
              position: 'relative'
            }}
          >
            <SearchOutlined
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: styles.textColor.tertiary
              }}
            />
            <input
              type="text"
              onChange={(e) => onSearch(e.target.value)}
              value={searchQuery}
              placeholder={`搜索${title}功能...`}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                borderRadius: '4px',
                border: '1px solid #d9d9d9',
                outline: 'none',
                transition: 'all 0.2s ease',
              }}
            />
          </div>
        </div>
        <List
          grid={{
            gutter: 18,
            xs: 1,
            sm: 2,
            md: 2,
            lg: 3,
            xl: 4,
            xxl: 5
          }}
          dataSource={filteredItems}
          renderItem={(item) => <FunctionItemCard item={item}/>}
          loading={isLoading}
          locale={{emptyText: '暂无匹配的功能'}}
        />
      </Card>
    </div>
  );
};

// 功能服务内容组件 - 左右结构切换显示
const FunctionServicesContent: React.FC = () => {
  // 状态管理：当前选中的分类和搜索关键词
  const [activeCategoryKey, setActiveCategoryKey] = useState(categories[0].key);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // 获取当前选中的分类
  const activeCategory = categories.find(cat => cat.key === activeCategoryKey) || categories[0];

  // 处理分类切换
  const handleCategoryChange = (key: string) => {
    if (key !== activeCategoryKey) {
      setIsLoading(true);
      setActiveCategoryKey(key);
      setSearchQuery(''); // 切换分类时清空搜索
      // 模拟加载效果
      setTimeout(() => {
        setIsLoading(false);
      }, 200);
    }
  };

  return (
    <div className="function_box">
      <ProCard split="vertical" className={'function_box_card'}>
        <ProCard title="" colSpan="20%" style={{
          height: '700px'
        }}>
          <Typography.Title
            level={4}
            style={{
              marginBottom: 24,
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              alignItems: 'center',
              color: styles.textColor.primary
            }}
          >
            <HomeOutlined style={{marginRight: 8, color: styles.primaryColor}}/>
            功能服务介绍
            <span style={{
              fontSize: '16px',
              fontWeight: 'normal',
              color: styles.textColor.secondary
            }}>
            </span>
          </Typography.Title>
          <Menu
            mode="inline"
            selectedKeys={[activeCategoryKey]}
            onClick={(info) => handleCategoryChange(info.key)}
            style={{
              height: '600px',
              background: styles.neutralColor,
              borderRadius: '8px',
              border: 'none',
              boxShadow: 'inset 0 0 2px rgba(0, 0, 0, 0.03)'
            }}
            inlineCollapsed={false}
            items={categories.map(category => ({
              key: category.key,
              icon: category.icon,
              label: category.title
            }))}
          />
        </ProCard>
        <ProCard title="" headerBordered style={{
          height: '700px', overflowY: 'auto'
        }}>
          <FunctionList
            title={activeCategory.title}
            items={activeCategory.items}
            isLoading={isLoading}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
          />
        </ProCard>
      </ProCard>
    </div>
  );
};

export default FunctionServicesContent;
